import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { DraftNotice } from '@/components/DraftNotice';
import { HistoryPanel } from '@/components/documents';
import { StatePill } from '@/components/hr/StatePill';
import { stateLabel } from '@/components/hr/states';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  Select,
  TextArea,
  errorMessage,
  money,
  shortDate,
  todayIso,
} from '@/components/ui';
import { formatDateTime } from '@/format';
import {
  FREQUENCY_TEXT,
  addDaysIso,
  hrGet,
  monthBounds,
  periodLabel,
  useFarmOptions,
  useHrInvalidation,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { useDraft } from '@/stores/drafts';
import { useScreenIntent } from '@/stores/shellNav';
import { PayrollRun, type RunRow } from './PayrollRun';
import { kindText } from './payrollShared';
import './payroll.css';

const PAGE_SIZE = 50;

const STAGE_FILTERS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Waiting for approval' },
  { value: 'APPROVED', label: 'Approved — to post' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

/**
 * Payroll & Payslips: the list of pay runs, a full-page form to start one,
 * and — once a run is opened — its workbench (PayrollRun.tsx).
 */
export function Payroll() {
  const intent = useScreenIntent('hr-pay');
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const thisYear = Number(todayIso().slice(0, 4));

  const [stage, setStage] = useState('');
  const [year, setYear] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('summary');
  const [openId, setOpenId] = useState<string | null>(intent?.recordId ?? null);
  const [creating, setCreating] = useState(intent?.action === 'create');
  const [note, setNote] = useState<string | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'payroll-runs', stage, year, skip],
    queryFn: () =>
      hrGet<{ total: number; items: RunRow[] }>('/api/v1/hr/payroll-runs', { stage, year, skip, take: PAGE_SIZE }),
    enabled: openId === null && !creating,
  });
  const rows = list.data?.items ?? [];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  const remove = useMutation({
    mutationFn: (row: RunRow) => api.delete(`/api/v1/hr/payroll-runs/${row.id}?version=${row.version}`),
    onSuccess: (_, row) => {
      invalidate();
      setSelectedKey(null);
      setProblem(null);
      setNote(`${row.documentNo} deleted.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not delete it')),
  });

  function open(row: RunRow) {
    setRunNote(null);
    setOpenId(row.id);
  }

  if (creating) {
    return (
      <div className="workbench">
        <div className="workbench__main workbench__main--form">
          <NewRunForm
            onCancel={() => setCreating(false)}
            onCreated={(id, documentNo) => {
              invalidate();
              setCreating(false);
              setRunNote(`${documentNo} started and worked out. Check it, then send it for approval.`);
              setOpenId(id);
            }}
          />
        </div>
      </div>
    );
  }

  if (openId) {
    return (
      <PayrollRun
        key={openId}
        runId={openId}
        initialNote={runNote}
        onBack={() => {
          setSelectedKey(openId);
          setOpenId(null);
        }}
      />
    );
  }

  function rowActions(row: RunRow): DesktopAction[] {
    return [
      { label: 'Open', icon: 'payroll', shortcut: 'Enter', onSelect: () => open(row) },
      {
        label: 'Delete draft',
        icon: 'remove',
        danger: true,
        shortcut: 'Del',
        onSelect: () => {
          if (window.confirm(`Delete ${row.documentNo}? It is only a draft, so nothing else changes.`)) remove.mutate(row);
        },
        allowed: can('hr.payroll.delete'),
        enabled: row.stage === 'DRAFT',
        reason: 'Only a draft pay run can be deleted. Reverse a posted one instead.',
        separatorBefore: true,
      },
      {
        label: 'History',
        icon: 'history',
        onSelect: () => {
          setSelectedKey(row.id);
          setTab('history');
        },
        separatorBefore: true,
      },
    ];
  }

  const toolbar: DesktopAction[] = [
    {
      label: 'New pay run',
      icon: 'add',
      primary: true,
      shortcut: 'Ins',
      onSelect: () => setCreating(true),
      allowed: can('hr.payroll.create'),
    },
    ...(selected
      ? rowActions(selected)
      : [
          { label: 'Open', icon: 'payroll' as const, onSelect: () => {}, enabled: false, reason: 'Choose a pay run first', separatorBefore: true },
          {
            label: 'Delete draft',
            icon: 'remove' as const,
            onSelect: () => {},
            allowed: can('hr.payroll.delete'),
            enabled: false,
            reason: 'Choose a pay run first',
          },
        ]),
  ];

  const columns: Array<GridColumn<RunRow>> = [
    {
      key: 'doc',
      header: 'Number',
      width: 150,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind="payroll" size={14} />
          <span className="mono">{r.documentNo}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      width: 170,
      render: (r) => (
        <span>
          {kindText(r.kind)}
          {r.title && <span className="dim"> — {r.title}</span>}
        </span>
      ),
    },
    { key: 'group', header: 'Pay group', width: 100, render: (r) => FREQUENCY_TEXT[r.payGroup] },
    { key: 'period', header: 'Period', width: 150, render: (r) => periodLabel(r.periodStart, r.periodEnd) },
    { key: 'farm', header: 'Farm', width: 170, render: (r) => <span className="dim">{r.farm}</span> },
    { key: 'people', header: 'People', width: 70, align: 'end', render: (r) => r.headcount },
    { key: 'gross', header: 'Gross', width: 120, align: 'end', render: (r) => money(r.totalGross) },
    { key: 'ded', header: 'Deductions', width: 110, align: 'end', render: (r) => money(r.totalDeduction) },
    { key: 'net', header: 'Net pay', width: 120, align: 'end', render: (r) => <strong>{money(r.totalNet)}</strong> },
    { key: 'cost', header: 'Company cost', width: 110, align: 'end', render: (r) => money(r.totalEmployerCost) },
    { key: 'stage', header: 'Stage', width: 180, render: (r) => <StatePill state={r.stage} /> },
    {
      key: 'paid',
      header: 'Paid',
      width: 120,
      render: (r) =>
        r.fullyPaid ? (
          <StatePill state="PAID" label="Fully paid" />
        ) : r.stage === 'POSTED' ? (
          `${r.paidCount} of ${r.headcount}`
        ) : (
          <span className="dim">—</span>
        ),
    },
  ];

  const tabs: DetailTab[] = selected
    ? [
        {
          id: 'summary',
          label: 'Summary',
          icon: 'details',
          content: (
            <dl className="detaillist">
              <dt>Kind</dt>
              <dd>
                {kindText(selected.kind)}
                {selected.title ? ` — ${selected.title}` : ''}
              </dd>
              <dt>Pay group</dt>
              <dd>{FREQUENCY_TEXT[selected.payGroup]}</dd>
              <dt>Period</dt>
              <dd>
                {periodLabel(selected.periodStart, selected.periodEnd)} ({shortDate(selected.periodStart)} to{' '}
                {shortDate(selected.periodEnd)})
              </dd>
              <dt>Farm</dt>
              <dd>{selected.farm}</dd>
              <dt>Stage</dt>
              <dd>
                <StatePill state={selected.stage} />
              </dd>
              <dt>People</dt>
              <dd>
                {selected.headcount}
                {selected.heldCount > 0 ? ` (${selected.heldCount} on hold)` : ''}
              </dd>
              <dt>Gross</dt>
              <dd>{money(selected.totalGross)}</dd>
              <dt>Deductions</dt>
              <dd>{money(selected.totalDeduction)}</dd>
              <dt>Net pay</dt>
              <dd>
                <strong>{money(selected.totalNet)}</strong>
              </dd>
              <dt>Company cost</dt>
              <dd>{money(selected.totalEmployerCost)}</dd>
              {selected.stage === 'POSTED' && (
                <>
                  <dt>Paid</dt>
                  <dd>
                    {selected.paidCount} of {selected.headcount}
                  </dd>
                </>
              )}
              {selected.calculatedAt && (
                <>
                  <dt>Worked out</dt>
                  <dd>{formatDateTime(selected.calculatedAt)}</dd>
                </>
              )}
              {selected.postedAt && (
                <>
                  <dt>Posted</dt>
                  <dd>{formatDateTime(selected.postedAt)}</dd>
                </>
              )}
              {selected.decisionNote && (
                <>
                  <dt>Decision note</dt>
                  <dd>{selected.decisionNote}</dd>
                </>
              )}
              {selected.reversalReason && (
                <>
                  <dt>Reversed because</dt>
                  <dd>{selected.reversalReason}</dd>
                </>
              )}
              {selected.notes && (
                <>
                  <dt>Notes</dt>
                  <dd>{selected.notes}</dd>
                </>
              )}
            </dl>
          ),
        },
        {
          id: 'history',
          label: 'History',
          icon: 'history',
          content: <HistoryPanel path={`/api/v1/hr/payroll-runs/${selected.id}/history`} />,
        },
      ]
    : [];

  const waiting = rows.filter((r) => r.stage === 'SUBMITTED').length;
  const toPost = rows.filter((r) => r.stage === 'APPROVED').length;

  return (
    <div className="workbench">
      <SplitView
        id="hr-payroll"
        direction="column"
        initial={420}
        firstLabel="pay run list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <Select
                label="Show"
                value={stage}
                onChange={(v) => {
                  setStage(v);
                  setSkip(0);
                }}
                options={STAGE_FILTERS}
                placeholder="Every stage"
              />
              <Select
                label="Year"
                value={year}
                onChange={(v) => {
                  setYear(v);
                  setSkip(0);
                }}
                options={Array.from({ length: 6 }, (_, i) => String(thisYear - i)).map((y) => ({ value: y, label: y }))}
                placeholder="Any year"
              />
            </div>
            <ActionBar actions={toolbar} />
            {note && <OkNote message={note} />}
            {problem && <ErrorNote message={problem} />}
            {list.error && <ErrorNote message={list.error.message} />}
            <DataGrid
              id="hr-payroll-runs"
              label="Pay runs"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={(k) => {
                setSelectedKey(k);
                setNote(null);
              }}
              onOpen={open}
              onDelete={(r) => {
                if (r.stage === 'DRAFT' && can('hr.payroll.delete') && window.confirm(`Delete ${r.documentNo}?`)) {
                  remove.mutate(r);
                }
              }}
              actions={rowActions}
              loading={list.isPending}
              empty={
                stage || year
                  ? 'No pay runs match these filters.'
                  : 'No pay runs yet. Start one with "New pay run".'
              }
            />
            <Pager skip={skip} take={PAGE_SIZE} total={list.data?.total ?? 0} onChange={setSkip} />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a pay run to see its totals. Press Enter or double-click to open it."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind="payroll" size={14} />
                  <span className="mono">{selected.documentNo}</span>{' '}
                  {periodLabel(selected.periodStart, selected.periodEnd)}
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>{list.data?.total ?? 0} pay runs</span>
        {waiting > 0 && <span>{waiting} waiting for approval</span>}
        {toPost > 0 && <span>{toPost} approved, not posted</span>}
        <span>{selected ? `Chosen: ${selected.documentNo} (${stateLabel(selected.stage)})` : 'Nothing chosen'}</span>
      </StatusBar>
    </div>
  );
}

type Kind = 'REGULAR' | 'OFF_CYCLE';
type Group = 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/** A new pay run. It is worked out on the server as soon as it is saved. */
function NewRunForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: string, documentNo: string) => void;
}) {
  const { user } = useAuth();
  const allFarms = user?.scope.allFarms ?? false;
  const farms = useFarmOptions();
  const today = todayIso();
  const [ty, tm] = today.split('-').map(Number);
  const lastMonth = tm === 1 ? `${ty! - 1}-12` : `${ty}-${pad(tm! - 1)}`;
  const thisMonth = monthBounds(ty!, tm!);
  const defaultStart = (g: Group) => addDaysIso(today, g === 'FORTNIGHTLY' ? -14 : -7);

  const [v, setV] = useState({
    kind: 'REGULAR' as Kind,
    payGroup: 'MONTHLY' as Group,
    month: lastMonth,
    start: defaultStart('WEEKLY'),
    end: thisMonth.end,
    farmId: '',
    title: '',
    notes: '',
  });
  const [problem, setProblem] = useState<string | null>(null);

  // A pay run is a handful of choices, but getting the period wrong is worth
  // avoiding twice over.
  const [blankValues] = useState(() => v);
  const draft = useDraft('payroll.new', v, (saved) => setV(saved));

  function startAgain() {
    setV(blankValues);
    setProblem(null);
  }

  const set = (key: keyof typeof v) => (value: string) => setV((s) => ({ ...s, [key]: value }));

  const wholeMonth = v.kind === 'REGULAR' && v.payGroup === 'MONTHLY';
  const fixedLength = v.kind === 'REGULAR' && v.payGroup !== 'MONTHLY';

  function period(): { start: string; end: string } | null {
    if (wholeMonth) {
      if (!/^\d{4}-\d{2}$/.test(v.month)) return null;
      const [y, m] = v.month.split('-').map(Number);
      return monthBounds(y!, m!);
    }
    if (!v.start) return null;
    if (fixedLength) return { start: v.start, end: addDaysIso(v.start, v.payGroup === 'WEEKLY' ? 6 : 13) };
    return v.end ? { start: v.start, end: v.end } : null;
  }
  const p = period();

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string; documentNo: string }>('/api/v1/hr/payroll-runs', {
        kind: v.kind,
        payGroup: v.payGroup,
        periodStart: p!.start,
        periodEnd: p!.end,
        farmId: v.farmId || null,
        ...(v.title.trim() ? { title: v.title.trim() } : {}),
        ...(v.notes.trim() ? { notes: v.notes.trim() } : {}),
      }),
    onSuccess: (r) => {
      draft.forget();
      onCreated(r.id, r.documentNo);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not start the pay run')),
  });

  function submit() {
    if (!p) return setProblem(wholeMonth ? 'Choose the month.' : 'Choose the dates.');
    if (p.end < p.start) return setProblem('The period cannot end before it starts.');
    if (!allFarms && !v.farmId) return setProblem('Choose the farm.');
    setProblem(null);
    save.mutate();
  }

  return (
    <FormPanel
      title="Start a new pay run"
      icon={<AppIcon kind="payroll" />}
      intro="It is worked out straight away from attendance, approved pay, fixed items, advances and loans. You can check and adjust it before sending it for approval."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DraftNotice draft={draft} onStartAgain={startAgain} />

        <FormGroup title="What kind of pay run" columns={2}>
          <Select
            label="Kind"
            required
            value={v.kind}
            onChange={(k) =>
              setV((s) =>
                k === 'OFF_CYCLE'
                  ? { ...s, kind: 'OFF_CYCLE', start: thisMonth.start, end: thisMonth.end }
                  : { ...s, kind: 'REGULAR', start: defaultStart(s.payGroup) },
              )
            }
            options={[
              { value: 'REGULAR', label: 'Regular pay' },
              { value: 'OFF_CYCLE', label: 'Off-cycle — bonus or arrears only' },
            ]}
            placeholder=""
            hint={
              v.kind === 'OFF_CYCLE'
                ? 'Only the bonus or arrears you add are paid. No salary, attendance or advances.'
                : 'Salary or wages for the period.'
            }
          />
          <Select
            label="Pay group"
            required
            value={v.payGroup}
            onChange={(g) => setV((s) => ({ ...s, payGroup: g as Group, ...(s.kind === 'REGULAR' ? { start: defaultStart(g as Group) } : {}) }))}
            options={[
              { value: 'MONTHLY', label: 'Monthly' },
              { value: 'FORTNIGHTLY', label: 'Fortnightly' },
              { value: 'WEEKLY', label: 'Weekly' },
            ]}
            placeholder=""
            hint="People are paid in the run that matches how often they are paid."
          />
        </FormGroup>

        <FormGroup
          title="Period"
          columns={2}
          hint={p && p.end >= p.start ? `Covers ${periodLabel(p.start, p.end)}.` : undefined}
        >
          {wholeMonth ? (
            <Field label="Month" type="month" required value={v.month} onChange={set('month')} hint="The whole calendar month." />
          ) : (
            <>
              <Field label="First day" type="date" required value={v.start} onChange={set('start')} />
              {fixedLength ? (
                <Field
                  label="Last day"
                  type="date"
                  value={p?.end ?? ''}
                  onChange={() => {}}
                  disabled
                  hint={v.payGroup === 'WEEKLY' ? '7 days in all.' : '14 days in all.'}
                />
              ) : (
                <Field label="Last day" type="date" required value={v.end} onChange={set('end')} />
              )}
            </>
          )}
        </FormGroup>

        <FormGroup title="Who and why" columns={2}>
          <Select
            label="Farm"
            required={!allFarms}
            value={v.farmId}
            onChange={set('farmId')}
            options={(farms.data ?? []).filter((f) => f.isActive).map((f) => ({ value: f.id, label: `${f.code} — ${f.nameEn}` }))}
            placeholder={allFarms ? 'All farms and head office' : undefined}
          />
          <Field
            label="Title"
            value={v.title}
            onChange={set('title')}
            placeholder={v.kind === 'OFF_CYCLE' ? 'e.g. Eid bonus' : 'Optional'}
          />
          <TextArea label="Notes" value={v.notes} onChange={set('notes')} rows={2} />
        </FormGroup>

        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Working it out…' : 'Start and work it out'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
